import { useEffect, useMemo, useState } from "react";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, Edit, TrendingUp, TrendingDown, Copy, FileDown, Package } from "lucide-react";
import { toast } from "sonner";
import { formatMoney } from "@/lib/money";
import { PageLoader } from "@/components/Spinner";
import { EmptyState } from "@/components/EmptyState";

// Unit groups for ingredient measurement
const UNIDADES = {
  Cantidad: ["unidad", "docena", "par", "ciento", "millar"],
  Peso: ["mg", "g", "kg", "ton", "oz", "lb"],
  Volumen: ["ml", "L", "m³", "fl oz", "gal"],
};

export default function Costing() {
  const [costings, setCostings] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCosting, setEditingCosting] = useState(null);
  // Mode: "existing" (link to existing product) | "new" (free-text name).
  const [productMode, setProductMode] = useState("existing");
  const [selectedProductId, setSelectedProductId] = useState("");
  const [selectedProductName, setSelectedProductName] = useState("");
  const [ingredientes, setIngredientes] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [quickEdit, setQuickEdit] = useState(null);  // { costingId, index, draft }
  const [currentIngredient, setCurrentIngredient] = useState({
    nombre: "",
    costo_unitario: 0,
    cantidad: 1,
    unidad: "unidad",
    subtotal: 0
  });

  // Memoised: each render of the summary table chained
  // [...costings].map(find→...).map(...).sort(...) which was O(n·m) per
  // re-render. Recompute only when costings or products actually change.
  const costingsSummary = useMemo(() => {
    return [...costings]
      .map(c => {
        const product = products.find(p => p.id === c.producto_id);
        const override = parseFloat(c.precio_venta_override) || 0;
        const precio = override > 0 ? override : parseFloat(product?.precio_venta || 0);
        const costo = parseFloat(c.costo_total || 0);
        return { c, product, precio, costo, utilidad: precio - costo };
      })
      .sort((a, b) => b.utilidad - a.utilidad);
  }, [costings, products]);

  useEffect(() => {
    fetchCostings();
    fetchProducts();
  }, []);

  const fetchCostings = async () => {
    try {
      const response = await api.get("/costings");
      setCostings(response.data);
    } catch (error) {
      toast.error("Error al cargar costeos");
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await api.get("/products");
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching products");
    }
  };

  const handleProductSelect = (productId) => {
    const product = products.find(p => p.id === productId);
    if (product) {
      setSelectedProductId(productId);
      setSelectedProductName(product.descripcion);
    }
  };

  const handleIngredientChange = (field, value) => {
    const updated = { ...currentIngredient, [field]: value };
    if (field === "costo_unitario" || field === "cantidad") {
      updated.subtotal = (parseFloat(updated.costo_unitario) || 0) * (parseFloat(updated.cantidad) || 0);
    }
    setCurrentIngredient(updated);
  };

  const addIngredient = () => {
    if (!currentIngredient.nombre || currentIngredient.costo_unitario <= 0 || currentIngredient.cantidad <= 0) {
      toast.error("Por favor completa todos los campos del ingrediente");
      return;
    }
    setIngredientes([...ingredientes, { ...currentIngredient, unidad: currentIngredient.unidad || "unidad" }]);
    setCurrentIngredient({
      nombre: "",
      costo_unitario: 0,
      cantidad: 1,
      unidad: "unidad",
      subtotal: 0
    });
  };

  const removeIngredient = (index) => {
    setIngredientes(ingredientes.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedName = (selectedProductName || "").trim();
    if (productMode === "existing" && !selectedProductId) {
      toast.error("Selecciona un producto existente");
      return;
    }
    if (productMode === "new" && !trimmedName) {
      toast.error("Ingresa el nombre del producto");
      return;
    }
    if (ingredientes.length === 0) {
      toast.error("Agrega al menos un ingrediente");
      return;
    }

    try {
      const costoTotal = ingredientes.reduce((sum, ing) => sum + ing.subtotal, 0);
      const data = {
        producto_id: productMode === "existing" ? selectedProductId : "",
        producto_nombre: trimmedName,
        ingredientes,
        costo_total: costoTotal
      };

      if (editingCosting) {
        await api.put(`/costings/${editingCosting.id}`, { ...data, id: editingCosting.id });
        toast.success(
          data.producto_id
            ? "Costeo actualizado y costo del producto sincronizado"
            : "Costeo actualizado"
        );
      } else {
        await api.post("/costings", data);
        toast.success(
          data.producto_id
            ? "Costeo creado y costo del producto sincronizado"
            : "Costeo creado"
        );
      }
      setIsDialogOpen(false);
      setEditingCosting(null);
      resetForm();
      fetchCostings();
      fetchProducts();
    } catch (error) {
      toast.error("Error al guardar costeo");
    }
  };

  const resetForm = () => {
    setProductMode("existing");
    setSelectedProductId("");
    setSelectedProductName("");
    setIngredientes([]);
    setCurrentIngredient({
      nombre: "",
      costo_unitario: 0,
      cantidad: 1,
      subtotal: 0
    });
  };

  const handleEdit = (costing) => {
    setEditingCosting(costing);
    // If the costing has a producto_id and that product still exists for this
    // user, lock to "existing" mode; otherwise expose it as free-text.
    const hasLinkedProduct =
      !!costing.producto_id && products.some(p => p.id === costing.producto_id);
    setProductMode(hasLinkedProduct ? "existing" : "new");
    setSelectedProductId(hasLinkedProduct ? costing.producto_id : "");
    setSelectedProductName(costing.producto_nombre);
    setIngredientes(costing.ingredientes);
    setIsDialogOpen(true);
  };

  const handleDelete = (id) => {
    setDeleteTargetId(id);
    setDeleteConfirmOpen(true);
  };

  const handleDuplicate = async (costing) => {
    try {
      const payload = {
        producto_id: costing.producto_id,
        producto_nombre: costing.producto_nombre,
        ingredientes: (costing.ingredientes || []).map(({ nombre, costo_unitario, cantidad, unidad, subtotal }) => ({
          nombre, costo_unitario, cantidad, unidad: unidad || "unidad", subtotal,
        })),
        costo_total: costing.costo_total,
      };
      await api.post("/costings", payload);
      toast.success("Costeo duplicado");
      fetchCostings();
    } catch {
      toast.error("Error al duplicar costeo");
    }
  };

  const startQuickEdit = (costing, index) => {
    const ing = costing.ingredientes[index];
    setQuickEdit({
      costingId: costing.id,
      index,
      draft: {
        nombre: ing.nombre,
        cantidad: ing.cantidad,
        unidad: ing.unidad || "unidad",
        costo_unitario: ing.costo_unitario,
      },
    });
  };

  const saveQuickEdit = async () => {
    if (!quickEdit) return;
    const costing = costings.find(c => c.id === quickEdit.costingId);
    if (!costing) return;
    const { cantidad, costo_unitario, unidad, nombre } = quickEdit.draft;
    const subtotal = Math.round((parseFloat(cantidad) || 0) * (parseFloat(costo_unitario) || 0) * 100) / 100;
    const updatedIngs = [...costing.ingredientes];
    updatedIngs[quickEdit.index] = {
      nombre,
      cantidad: parseFloat(cantidad) || 0,
      unidad,
      costo_unitario: parseFloat(costo_unitario) || 0,
      subtotal,
    };
    const newTotal = updatedIngs.reduce((s, i) => s + (i.subtotal || 0), 0);
    try {
      await api.put(`/costings/${costing.id}`, {
        producto_id: costing.producto_id,
        producto_nombre: costing.producto_nombre,
        ingredientes: updatedIngs,
        costo_total: newTotal,
      });
      toast.success("Ingrediente actualizado");
      setQuickEdit(null);
      fetchCostings();
    } catch {
      toast.error("Error al actualizar ingrediente");
    }
  };

  // Inline edit of sale price.
  // If the costing is linked to an existing Product, we update that product's
  // precio_venta. Otherwise we store an override on the costing itself.
  const [priceEdit, setPriceEdit] = useState(null); // { costingId, value }
  const startPriceEdit = (costing) => {
    if (!costing) return;
    const product = products.find(p => p.id === costing.producto_id);
    const current =
      costing.precio_venta_override > 0
        ? costing.precio_venta_override
        : (product?.precio_venta ?? 0);
    setPriceEdit({ costingId: costing.id, value: String(current || "") });
  };
  const savePriceEdit = async () => {
    if (!priceEdit) return;
    const costing = costings.find(c => c.id === priceEdit.costingId);
    if (!costing) {
      setPriceEdit(null);
      return;
    }
    const newPrice = parseFloat(priceEdit.value) || 0;
    if (newPrice < 0) {
      toast.error("El precio no puede ser negativo");
      return;
    }
    try {
      const product = products.find(p => p.id === costing.producto_id);
      if (product) {
        // Update the linked product's price + clear any override.
        await api.put(`/products/${product.id}`, { ...product, precio_venta: newPrice });
        if (costing.precio_venta_override) {
          await api.put(`/costings/${costing.id}`, {
            producto_id: costing.producto_id,
            producto_nombre: costing.producto_nombre,
            ingredientes: costing.ingredientes,
            costo_total: costing.costo_total,
            precio_venta_override: 0,
          });
        }
        fetchProducts();
      } else {
        // No product linked → persist as override on the costing.
        await api.put(`/costings/${costing.id}`, {
          producto_id: costing.producto_id,
          producto_nombre: costing.producto_nombre,
          ingredientes: costing.ingredientes,
          costo_total: costing.costo_total,
          precio_venta_override: newPrice,
        });
      }
      toast.success("Precio de venta actualizado");
      setPriceEdit(null);
      fetchCostings();
    } catch {
      toast.error("Error al actualizar precio");
    }
  };

  const handleExportPdf = async (costing) => {  try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/costings/${costing.id}/export`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const safeName = (costing.producto_nombre || "costeo").replace(/[^a-z0-9-_]/gi, "_");
      link.download = `costeo_${safeName}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF descargado");
    } catch {
      toast.error("Error al descargar PDF");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTargetId) return;
    try {
      await api.delete(`/costings/${deleteTargetId}`);
      toast.success("Costeo eliminado");
      setDeleteConfirmOpen(false);
      setDeleteTargetId(null);
      fetchCostings();
    } catch (error) {
      toast.error("Error al eliminar costeo");
    }
  };

  // Calcular margen de utilidad: ((precio_venta - costo_total) / precio_venta) * 100
  const calcularMargen = (costing) => {
    const product = products.find(p => p.id === costing.producto_id);
    const override = parseFloat(costing.precio_venta_override) || 0;
    const precioVenta = override > 0 ? override : (product?.precio_venta || 0);
    const costoTotal = costing.costo_total || 0;
    if (precioVenta === 0) return { utilidad: -costoTotal, margen: null, precioVenta };
    const utilidad = precioVenta - costoTotal;
    const margen = (utilidad / precioVenta) * 100;
    return { utilidad, margen, precioVenta };
  };

  if (loading) {
    return <PageLoader label="Cargando costeos…" testid="costing-loader" />;
  }

  return (
    <div data-testid="costing-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Costeo de Productos
          </h2>
          <p className="text-slate-500 text-sm mt-1">Gestiona los costos integrales de tus productos</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) {
            setEditingCosting(null);
            resetForm();
          }
        }}>
          <DialogTrigger asChild>
            <Button className="btn-primary rounded-sm" data-testid="add-costing-button">
              <Plus className="h-4 w-4 mr-2" />
              Nuevo Costeo
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingCosting ? "Editar Costeo" : "Nuevo Costeo"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium mb-2">Producto *</label>
                <div className="inline-flex rounded-sm border border-slate-200 bg-slate-50 p-0.5 mb-3" role="tablist" data-testid="costing-product-mode">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={productMode === "existing"}
                    data-testid="costing-mode-existing"
                    onClick={() => {
                      setProductMode("existing");
                      // Clear free-text-only state when switching back.
                      if (!products.find(p => p.id === selectedProductId)) {
                        setSelectedProductId("");
                        setSelectedProductName("");
                      }
                    }}
                    className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-all ${
                      productMode === "existing"
                        ? "bg-white text-[#002FA7] shadow-sm"
                        : "text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    Producto existente
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={productMode === "new"}
                    data-testid="costing-mode-new"
                    onClick={() => {
                      setProductMode("new");
                      setSelectedProductId("");
                    }}
                    className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-all ${
                      productMode === "new"
                        ? "bg-white text-[#002FA7] shadow-sm"
                        : "text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    Producto nuevo
                  </button>
                </div>
                {productMode === "existing" ? (
                  <Select value={selectedProductId} onValueChange={handleProductSelect}>
                    <SelectTrigger className="rounded-sm" data-testid="costing-product-select">
                      <SelectValue placeholder={products.length === 0 ? "Aún no tenés productos — usá Producto nuevo" : "Selecciona un producto"} />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map((product) => (
                        <SelectItem key={product.id} value={product.id}>
                          {product.codigo} - {product.descripcion}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <>
                    <Input
                      data-testid="costing-product-name-input"
                      placeholder="Ej: Pizza Margarita (todavía no creada)"
                      value={selectedProductName}
                      onChange={(e) => setSelectedProductName(e.target.value)}
                      className="rounded-sm"
                      autoFocus
                    />
                    <p className="text-[11px] text-slate-500 mt-1.5">
                      El costeo se guardará con este nombre. No se vincula con ningún producto del catálogo.
                    </p>
                  </>
                )}
                {productMode === "existing" && selectedProductId && (
                  <p className="text-[11px] text-slate-500 mt-1.5">
                    Al guardar, el <strong>costo</strong> de este producto se actualizará automáticamente con el costo total del costeo.
                  </p>
                )}
              </div>

              <div className="border border-slate-200 rounded-sm p-4">
                <h4 className="font-medium mb-3">Ingredientes / Partes</h4>
                <div className="grid grid-cols-14 gap-2 mb-3" style={{ gridTemplateColumns: "repeat(14, minmax(0, 1fr))" }}>
                  <div className="col-span-4">
                    <label className="block text-[10px] font-medium uppercase tracking-wider text-slate-500 mb-1">Nombre</label>
                    <Input
                      data-testid="ingredient-name-input"
                      placeholder="Ej: Tela algodón"
                      value={currentIngredient.nombre}
                      onChange={(e) => handleIngredientChange("nombre", e.target.value)}
                      className="rounded-sm"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-[10px] font-medium uppercase tracking-wider text-slate-500 mb-1">Cantidad</label>
                    <Input
                      data-testid="ingredient-quantity-input"
                      type="number"
                      step="0.01"
                      placeholder="0"
                      value={currentIngredient.cantidad}
                      onChange={(e) => handleIngredientChange("cantidad", parseFloat(e.target.value) || 0)}
                      className="rounded-sm"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-[10px] font-medium uppercase tracking-wider text-slate-500 mb-1">Unidad</label>
                    <Select value={currentIngredient.unidad} onValueChange={(v) => handleIngredientChange("unidad", v)}>
                      <SelectTrigger className="rounded-sm" data-testid="ingredient-unit-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(UNIDADES).map(([groupLabel, units]) => (
                          <SelectGroup key={groupLabel}>
                            <SelectLabel className="text-[10px] uppercase tracking-wider text-slate-500">{groupLabel}</SelectLabel>
                            {units.map(u => (
                              <SelectItem key={u} value={u}>{u}</SelectItem>
                            ))}
                          </SelectGroup>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-[10px] font-medium uppercase tracking-wider text-slate-500 mb-1">Costo Unit.</label>
                    <Input
                      data-testid="ingredient-cost-input"
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={currentIngredient.costo_unitario}
                      onChange={(e) => handleIngredientChange("costo_unitario", parseFloat(e.target.value) || 0)}
                      className="rounded-sm"
                    />
                  </div>
                  <div className="col-span-3">
                    <label className="block text-[10px] font-medium uppercase tracking-wider text-slate-500 mb-1">Subtotal</label>
                    <Input
                      value={formatMoney(currentIngredient.subtotal)}
                      disabled
                      className="rounded-sm bg-slate-50 font-medium"
                    />
                  </div>
                  <div className="col-span-1 flex items-end">
                    <Button
                      type="button"
                      onClick={addIngredient}
                      className="w-full btn-primary rounded-sm h-10"
                      data-testid="add-ingredient-button"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {ingredientes.length > 0 && (
                  <div className="border-t border-slate-200 pt-3">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-2">Nombre</th>
                          <th className="text-right py-2">Cantidad</th>
                          <th className="text-left py-2 pl-2">Unidad</th>
                          <th className="text-right py-2">Costo Unit.</th>
                          <th className="text-right py-2">Subtotal</th>
                          <th className="w-8"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {ingredientes.map((ing, index) => (
                          <tr key={index} className="border-b border-slate-100">
                            <td className="py-2">{ing.nombre}</td>
                            <td className="py-2 text-right">{ing.cantidad}</td>
                            <td className="py-2 pl-2 text-slate-600">{ing.unidad || "unidad"}</td>
                            <td className="py-2 text-right">{formatMoney(ing.costo_unitario)}</td>
                            <td className="py-2 text-right font-medium">{formatMoney(ing.subtotal)}</td>
                            <td className="py-2">
                              <Button
                                type="button"
                                onClick={() => removeIngredient(index)}
                                variant="ghost"
                                size="sm"
                                className="rounded-sm"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {ingredientes.length > 0 && (
                  <Card className="mt-4 p-4 bg-[rgb(0,47,167)] text-white">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-bold">Costo Total:</span>
                      <span className="text-2xl font-black" data-testid="total-cost">
                        {formatMoney(ingredientes.reduce((sum, ing) => sum + ing.subtotal, 0))}
                      </span>
                    </div>
                  </Card>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  className="rounded-sm"
                >
                  Cancelar
                </Button>
                <Button type="submit" className="btn-primary rounded-sm" data-testid="save-costing-button">
                  {editingCosting ? "Actualizar" : "Crear"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {costings.length === 0 ? (
          <div className="col-span-full">
            <EmptyState
              icon={Package}
              title="Aún no hay costeos"
              description="Calculá el costo real de tus productos con ingredientes y márgenes. Podés costear un producto existente o uno nuevo desde cero."
              actionLabel="Nuevo costeo"
              actionOnClick={() => { resetForm(); setIsDialogOpen(true); }}
              testid="costings-empty-state"
            />
          </div>
        ) : (
          costings.map((costing) => {
            const { utilidad, margen, precioVenta } = calcularMargen(costing);
            const esPositivo = utilidad >= 0;
            const colorClass = esPositivo ? 'text-green-600' : 'text-red-600';
            const bgClass = esPositivo ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200';

            return (
              <Card key={costing.id} className="bg-white border border-slate-200 rounded-sm p-6" data-testid="costing-card">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="font-bold text-lg mb-1">{costing.producto_nombre}</h3>
                    <p className="text-xs uppercase tracking-[0.2em] text-slate-400">
                      {costing.ingredientes.length} ingredientes
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      data-testid={`edit-costing-${costing.id}`}
                      onClick={() => handleEdit(costing)}
                      variant="ghost"
                      size="sm"
                      className="rounded-sm"
                      title="Editar"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      data-testid={`duplicate-costing-${costing.id}`}
                      onClick={() => handleDuplicate(costing)}
                      variant="ghost"
                      size="sm"
                      className="rounded-sm text-[rgb(0,47,167)] hover:bg-blue-50"
                      title="Duplicar"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                      data-testid={`export-costing-${costing.id}`}
                      onClick={() => handleExportPdf(costing)}
                      variant="ghost"
                      size="sm"
                      className="rounded-sm text-emerald-700 hover:bg-emerald-50"
                      title="Descargar PDF"
                    >
                      <FileDown className="h-4 w-4" />
                    </Button>
                    <Button
                      data-testid={`delete-costing-${costing.id}`}
                      onClick={() => handleDelete(costing.id)}
                      variant="ghost"
                      size="sm"
                      className="rounded-sm text-red-700 hover:text-red-800 hover:bg-red-50"
                      title="Eliminar"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="flex items-center justify-between text-[10px] uppercase tracking-wider font-bold text-slate-400 px-1 mb-1.5">
                  <span className="flex items-center gap-1">
                    <Edit className="h-3 w-3" />
                    Click un ingrediente para editar cantidad, unidad y precio
                  </span>
                  <span>Subtotal</span>
                </div>
                <div className="space-y-2 mb-4">
                  {costing.ingredientes.map((ing, index) => {
                    const isEditing = quickEdit && quickEdit.costingId === costing.id && quickEdit.index === index;
                    if (isEditing) {
                      const d = quickEdit.draft;
                      const liveSubtotal = (parseFloat(d.cantidad) || 0) * (parseFloat(d.costo_unitario) || 0);
                      return (
                        <div key={index} className="border border-[rgb(0,47,167)] bg-blue-50/40 rounded-sm p-2 space-y-1.5" data-testid="quick-edit-ingredient">
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] uppercase tracking-wider font-bold text-[rgb(0,47,167)]">Editando ingrediente</span>
                            <span className="text-[10px] font-semibold text-slate-700" data-testid="quick-edit-live-subtotal">
                              Subtotal: <span className="text-[rgb(0,47,167)]">{formatMoney(liveSubtotal)}</span>
                            </span>
                          </div>
                          <Input
                            value={d.nombre}
                            onChange={(e) => setQuickEdit({ ...quickEdit, draft: { ...d, nombre: e.target.value } })}
                            className="h-7 text-xs rounded-sm bg-white"
                            placeholder="Nombre"
                            data-testid="quick-edit-nombre"
                          />
                          <div className="grid grid-cols-3 gap-1.5">
                            <Input
                              type="number" step="0.01" min="0"
                              value={d.cantidad}
                              onChange={(e) => setQuickEdit({ ...quickEdit, draft: { ...d, cantidad: e.target.value } })}
                              className="h-7 text-xs rounded-sm bg-white"
                              placeholder="Cant."
                              data-testid="quick-edit-cantidad"
                            />
                            <Select value={d.unidad} onValueChange={(v) => setQuickEdit({ ...quickEdit, draft: { ...d, unidad: v } })}>
                              <SelectTrigger className="h-7 text-xs rounded-sm bg-white" data-testid="quick-edit-unidad">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {Object.entries(UNIDADES).map(([gl, units]) => (
                                  <SelectGroup key={gl}>
                                    <SelectLabel className="text-[10px] uppercase">{gl}</SelectLabel>
                                    {units.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                                  </SelectGroup>
                                ))}
                              </SelectContent>
                            </Select>
                            <Input
                              type="number" step="0.01" min="0"
                              value={d.costo_unitario}
                              onChange={(e) => setQuickEdit({ ...quickEdit, draft: { ...d, costo_unitario: e.target.value } })}
                              className="h-7 text-xs rounded-sm bg-white"
                              placeholder="Costo"
                              data-testid="quick-edit-costo"
                            />
                          </div>
                          <div className="flex items-center justify-end gap-1.5">
                            <Button type="button" size="sm" variant="outline" className="h-7 px-3 text-xs rounded-sm"
                              onClick={() => setQuickEdit(null)} data-testid="quick-edit-cancel">
                              Cancelar
                            </Button>
                            <Button type="button" size="sm" className="h-7 px-3 text-xs btn-primary rounded-sm"
                              onClick={saveQuickEdit} data-testid="quick-edit-save">
                              Guardar
                            </Button>
                          </div>
                        </div>
                      );
                    }
                    return (
                      <button
                        type="button"
                        key={index}
                        onClick={() => startQuickEdit(costing, index)}
                        className="group w-full flex justify-between items-center text-sm border-b border-slate-100 pb-2 gap-2 text-left hover:bg-blue-50/40 rounded-sm px-1.5 py-1 transition-colors"
                        title="Click para editar cantidad, unidad y precio"
                        data-testid={`ingredient-row-${costing.id}-${index}`}
                      >
                        <span className="text-slate-700 truncate flex-1 flex items-center gap-1.5">
                          <Edit className="h-3 w-3 text-slate-300 group-hover:text-[rgb(0,47,167)] flex-shrink-0" />
                          <span className="truncate">
                            {ing.nombre}
                            {ing.cantidad ? (
                              <span className="text-slate-500 ml-1.5 text-xs">({ing.cantidad} {ing.unidad || "unidad"})</span>
                            ) : null}
                          </span>
                        </span>
                        <span className="font-medium flex-shrink-0">{formatMoney(ing.subtotal)}</span>
                      </button>
                    );
                  })}
                </div>

                <div className="pt-3 border-t-2 border-slate-200 space-y-2">
                  {(() => {
                    const isEditingPrice = priceEdit && priceEdit.costingId === costing.id;
                    if (isEditingPrice) {
                      return (
                        <div className="bg-blue-50/40 border border-[rgb(0,47,167)] rounded-sm p-2 space-y-1.5" data-testid={`price-edit-${costing.id}`}>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-600 flex-shrink-0 w-24">Precio Venta:</span>
                            <Input
                              type="number" step="0.01" min="0"
                              value={priceEdit.value}
                              autoFocus
                              onChange={(e) => setPriceEdit({ ...priceEdit, value: e.target.value })}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") { e.preventDefault(); savePriceEdit(); }
                                if (e.key === "Escape") setPriceEdit(null);
                              }}
                              className="h-7 text-xs rounded-sm bg-white flex-1 min-w-0"
                              data-testid="price-edit-input"
                            />
                          </div>
                          <div className="flex items-center justify-end gap-1.5">
                            <Button type="button" size="sm" variant="outline"
                              className="h-7 px-3 text-xs rounded-sm"
                              onClick={() => setPriceEdit(null)}
                              data-testid="price-edit-cancel">
                              Cancelar
                            </Button>
                            <Button type="button" size="sm"
                              className="h-7 px-3 text-xs btn-primary rounded-sm"
                              onClick={savePriceEdit}
                              data-testid="price-edit-save">
                              Guardar
                            </Button>
                          </div>
                        </div>
                      );
                    }
                    return (
                      <button
                        type="button"
                        onClick={() => startPriceEdit(costing)}
                        title="Click para editar el precio de venta"
                        className="w-full flex justify-between items-center text-sm hover:bg-blue-50/40 rounded-sm px-1 py-1 transition-colors group"
                        data-testid={`price-row-${costing.id}`}
                      >
                        <span className="text-slate-600 flex items-center gap-1.5">
                          <Edit className="h-3 w-3 text-slate-300 group-hover:text-[rgb(0,47,167)]" />
                          Precio Venta:
                        </span>
                        <span className="font-semibold">{formatMoney(precioVenta)}</span>
                      </button>
                    );
                  })()}
                  <div className="flex justify-between items-center text-sm px-1">
                    <span className="text-slate-600">Costo Total:</span>
                    <span className="font-semibold text-[rgb(0,47,167)]">
                      {formatMoney(costing.costo_total)}
                    </span>
                  </div>
                </div>

                <div className={`mt-3 p-3 border rounded-sm ${bgClass}`} data-testid={`margin-${costing.id}`}>
                  <div className="flex justify-between items-center">
                    <span className="text-xs uppercase tracking-[0.2em] font-bold text-slate-600 flex items-center gap-1">
                      {esPositivo ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                      Margen Utilidad
                    </span>
                    <div className="text-right">
                      <div className={`text-xl font-black ${colorClass}`}>
                        {margen !== null ? `${margen.toFixed(1)}%` : 'N/A'}
                      </div>
                      <div className={`text-xs ${colorClass}`}>
                        {esPositivo ? '+' : ''}{formatMoney(utilidad)}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })
        )}
      </div>

      {/* Tabla resumen de productos costeados */}
      {costings.length > 0 && (
        <Card className="bg-white border border-slate-200 rounded-sm overflow-hidden mt-8" data-testid="costings-summary-table">
          <div className="flex items-center gap-2 px-5 py-4 border-b border-slate-200 bg-slate-50">
            <Package className="h-4 w-4 text-[rgb(0,47,167)]" />
            <h3 className="text-sm font-semibold tracking-wider uppercase text-slate-700">Resumen de productos costeados</h3>
            <span className="text-xs text-slate-500 ml-auto">{costings.length} producto{costings.length === 1 ? "" : "s"}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left py-3 px-4 font-medium text-slate-700">Producto</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-700">Ingredientes</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-700">Costo Total</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-700">Precio Venta</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-700">Utilidad</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-700">Margen</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-700">PDF</th>
                </tr>
              </thead>
              <tbody>
                {costingsSummary.map(({ c, product, precio, costo, utilidad }) => {
                  const util = utilidad;
                  const marg = precio > 0 ? (util / precio) * 100 : null;
                  const pos = util >= 0;
                  const missingProduct = !product;
                  return (
                    <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid="costing-summary-row">
                      <td className="py-3 px-4">
                        <div className="font-medium text-slate-900 flex items-center gap-1.5">
                          {c.producto_nombre}
                          {missingProduct && (
                            <span className="text-[9px] uppercase tracking-wider px-1 py-0.5 bg-amber-50 text-amber-800 border border-amber-200 rounded-sm" title="El producto vinculado fue eliminado">
                              sin producto
                            </span>
                          )}
                        </div>
                        {product?.codigo && <div className="text-[10px] text-slate-500 mono">{product.codigo}</div>}
                      </td>
                      <td className="py-3 px-4 text-right text-slate-600">{c.ingredientes?.length || 0}</td>
                      <td className="py-3 px-4 text-right font-medium text-[rgb(0,47,167)]">{formatMoney(costo)}</td>
                      <td className="py-3 px-4 text-right text-slate-900">
                        {precio > 0 ? formatMoney(precio) : <span className="text-slate-400 text-xs">sin precio</span>}
                      </td>
                      <td className={`py-3 px-4 text-right font-medium ${precio > 0 ? (pos ? "text-emerald-700" : "text-red-700") : "text-slate-400"}`}>
                        {precio > 0 ? `${pos ? "+" : ""}${formatMoney(util)}` : "—"}
                      </td>
                      <td className={`py-3 px-4 text-right font-bold ${precio > 0 ? (pos ? "text-emerald-700" : "text-red-700") : "text-slate-400"}`}>
                        {marg !== null ? `${marg.toFixed(1)}%` : "—"}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Button
                          onClick={() => handleExportPdf(c)}
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 text-emerald-700 hover:bg-emerald-50 rounded-sm"
                          data-testid={`summary-export-${c.id}`}
                        >
                          <FileDown className="h-3.5 w-3.5" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Confirmar eliminación?</AlertDialogTitle>
            <AlertDialogDescription>
              Estás a punto de eliminar este costeo. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-costing-button">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-costing-button"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
